import React, { useEffect, useMemo, useRef, useState } from "react";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Checkbox,
  Typography,
} from "@mui/material";
import { VerticalAssignmentErrorResponse } from "../../services/verticalAssignmentService";
import { mainFont } from "../../utils/StyleHelpers";

export type AssignmentReadbackState =
  | { status: "loading" }
  | { status: "loaded"; verticalIds: string[] }
  | { status: "error"; message: string };

interface AssignmentDialogTaxonomy {
  available: boolean;
  verticals: Array<{ id: string; name: string }>;
}

export interface AssignmentPayload {
  videoIndexIds: string[];
  verticalIds: string[];
}

interface AssignmentVideo {
  videoIndexId: string;
  name: string;
  image?: string;
}

export interface VideoAssignmentDialogProps {
  open: boolean;
  videoName?: string;
  videoIndexId?: string;
  videoIndexIds?: string[];
  selectedVideos?: AssignmentVideo[];
  taxonomy: AssignmentDialogTaxonomy;
  taxonomyStatus: "loading" | "loaded" | "error";
  taxonomyError: string | null;
  readback?: AssignmentReadbackState;
  onClose: () => void;
  onRetryTaxonomy: () => void;
  onRetryReadback?: () => void;
  onSubmit: (payload: AssignmentPayload) => Promise<unknown>;
  saving: boolean;
  dialogError: VerticalAssignmentErrorResponse | null;
}

const VideoAssignmentDialog: React.FC<VideoAssignmentDialogProps> = ({
  open,
  videoName = "",
  videoIndexId = "",
  videoIndexIds,
  selectedVideos,
  taxonomy,
  taxonomyStatus,
  taxonomyError,
  readback,
  onClose,
  onRetryTaxonomy,
  onRetryReadback,
  onSubmit,
  saving,
  dialogError,
}) => {
  const titleId = "vertical-assignment-title";
  const bodyId = "vertical-assignment-body";
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const inFlightRef = useRef(false);
  const [selectedVerticalIds, setSelectedVerticalIds] = useState<string[]>([]);
  const [localSaving, setLocalSaving] = useState(false);

  const videos = useMemo<AssignmentVideo[]>(() => {
    if (selectedVideos?.length) return selectedVideos;
    return videoIndexId ? [{ videoIndexId, name: videoName }] : [];
  }, [selectedVideos, videoIndexId, videoName]);

  const ids = useMemo(
    () => (videoIndexIds?.length ? videoIndexIds : videos.map((video) => video.videoIndexId)),
    [videoIndexIds, videos],
  );

  useEffect(() => {
    if (!open) return;
    inFlightRef.current = false;
    setLocalSaving(false);
    setSelectedVerticalIds(readback?.status === "loaded" ? readback.verticalIds : []);
    window.requestAnimationFrame(() => titleRef.current?.focus());
  }, [open, readback]);

  const busy = saving || localSaving;
  const unavailable = taxonomyStatus !== "loaded" || !taxonomy.available;
  const readbackUnavailable = !!readback && readback.status !== "loaded";
  const pickerDisabled = busy || readbackUnavailable;
  const selectionRequired = !readback;
  const submitDisabled =
    busy ||
    unavailable ||
    readbackUnavailable ||
    taxonomy.verticals.length === 0 ||
    (selectionRequired && selectedVerticalIds.length === 0);

  const toggleVertical = (id: string) => {
    if (pickerDisabled) return;
    setSelectedVerticalIds((current) =>
      current.includes(id) ? current.filter((verticalId) => verticalId !== id) : [...current, id],
    );
  };

  const handleSubmit = async () => {
    if (submitDisabled || inFlightRef.current) return;
    inFlightRef.current = true;
    setLocalSaving(true);
    try {
      await onSubmit({
        videoIndexIds: ids,
        verticalIds: taxonomy.verticals
          .map((vertical) => vertical.id)
          .filter((verticalId) => selectedVerticalIds.includes(verticalId)),
      });
    } finally {
      inFlightRef.current = false;
      setLocalSaving(false);
    }
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      disableAutoFocus
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") handleClose();
      }}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      PaperProps={{
        sx: {
          width: "1000px",
          maxWidth: "calc(100% - 64px)",
          minHeight: { xs: "auto", md: "608px" },
          maxHeight: "calc(100% - 64px)",
          borderRadius: "20px",
          fontFamily: mainFont,
        },
      }}
    >
      <DialogTitle
        id={titleId}
        ref={titleRef}
        tabIndex={-1}
        sx={{
          p: "40px 40px 18px",
          fontSize: "24px",
          fontWeight: 500,
          lineHeight: "29px",
          outline: "none",
        }}
      >
        Assign to Vertical
      </DialogTitle>

      <DialogContent id={bodyId} sx={{ p: "0 40px !important", overflow: "hidden" }}>
        {readback?.status === "loading" ? (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Loading current assignment...</Typography>
          </Box>
        ) : readback?.status === "error" ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              onRetryReadback ? (
                <Button color="inherit" size="small" onClick={onRetryReadback}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {readback.message || "Failed to load current assignment."}
          </Alert>
        ) : null}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "12px",
          }}
        >
          <Box
            sx={{
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              height: { xs: "260px", md: "400px" },
              overflow: "auto",
            }}
          >
            <List disablePadding aria-label="Selected videos">
              {videos.map((video) => (
                <ListItem
                  key={video.videoIndexId}
                  sx={{ px: "20px", minHeight: "72px", gap: 2, borderBottom: "1px solid #EEF1F4" }}
                >
                  {video.image ? (
                    <img
                      src={video.image}
                      alt={video.name}
                      style={{
                        width: "2.5rem",
                        alignSelf: "stretch",
                        objectFit: "cover",
                        borderRadius: "0.25rem",
                      }}
                    />
                  ) : (
                    <VideocamOutlinedIcon aria-hidden="true" sx={{ color: "#0265DC" }} />
                  )}
                  <Typography noWrap title={video.name}>
                    {video.name}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box
            sx={{
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              height: { xs: "260px", md: "400px" },
              overflow: "auto",
            }}
          >
            {taxonomyStatus === "loading" ? (
              <Box sx={{ px: 2, py: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography>Loading verticals...</Typography>
              </Box>
            ) : taxonomyStatus === "error" ? (
              <Alert
                severity="error"
                sx={{ m: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={onRetryTaxonomy}>
                    Retry
                  </Button>
                }
              >
                {taxonomyError || "Failed to load verticals."}
              </Alert>
            ) : taxonomy.verticals.length === 0 || !taxonomy.available ? (
              <Typography sx={{ px: 2, py: 3 }}>No verticals are available for assignment.</Typography>
            ) : (
              <List disablePadding aria-label="Verticals">
                {taxonomy.verticals.map((vertical) => {
                  const selected = selectedVerticalIds.includes(vertical.id);
                  return (
                    <ListItem
                      key={vertical.id}
                      onClick={() => toggleVertical(vertical.id)}
                      sx={{
                        px: "20px",
                        height: "40px",
                        cursor: "pointer",
                        backgroundColor: selected ? "#C1E7FE" : "transparent",
                      }}
                    >
                      <Typography sx={{ flex: 1 }}>{vertical.name}</Typography>
                      <Checkbox
                        edge="end"
                        name={vertical.name}
                        checked={selected}
                        onChange={() => toggleVertical(vertical.id)}
                        onClick={(event) => event.stopPropagation()}
                        disabled={pickerDisabled}
                        inputProps={{ "aria-label": vertical.name }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        {dialogError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {dialogError.message || "Assignment could not be completed."}
            {dialogError.details?.invalidVerticalIds?.length
              ? ` ${dialogError.details.invalidVerticalIds.join(", ")}`
              : ""}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions disableSpacing sx={{ p: "24px 40px 40px", gap: "12px" }}>
        <Button
          variant="contained"
          color="info"
          onClick={handleClose}
          disabled={busy}
          sx={{
            flex: 1,
            height: "56px",
            borderRadius: "8px",
            fontSize: "20px",
            fontWeight: 500,
            color: "#000",
            backgroundColor: "#C1E7FE",
            "&:hover": { backgroundColor: "#A9DAF8" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          aria-busy={busy}
          sx={{
            flex: 1,
            height: "56px",
            borderRadius: "8px",
            fontSize: "20px",
            fontWeight: 500,
            backgroundColor: "#0265DC",
          }}
        >
          {busy ? <CircularProgress size={24} color="inherit" /> : "Assign Videos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoAssignmentDialog;
